import React, { lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import HomePage from "./pages/HomePage.jsx";
import { Toaster } from "./components/ui/toaster";
import { ArrowLeft } from "lucide-react";

// ✅ Pages légales lazy-loaded (rarement visitées)
const MentionsLegales = lazy(() => import("./pages/MentionsLegales.jsx"));
const PolitiqueConfidentialite = lazy(() => import("./pages/PolitiqueConfidentialite.jsx"));
const CGV = lazy(() => import("./pages/CGV.jsx"));

// ✅ Pages SEO locales par ville (lazy-loaded)
const CreationSiteHayange = lazy(() => import("./pages/CreationSiteHayange.jsx"));
const CreationSiteMetz = lazy(() => import("./pages/CreationSiteMetz.jsx"));
const CreationSiteYutz = lazy(() => import("./pages/CreationSiteYutz.jsx"));
const CreationSiteFameck = lazy(() => import("./pages/CreationSiteFameck.jsx"));
const CreationSiteAmneville = lazy(() => import("./pages/CreationSiteAmneville.jsx"));
const CreationSiteFlorange = lazy(() => import("./pages/CreationSiteFlorange.jsx"));
const CreationSiteTerville = lazy(() => import("./pages/CreationSiteTerville.jsx"));
const CreationSiteThionville = lazy(() => import("./pages/CreationSiteThionville.jsx"));
const CreationSiteUckange = lazy(() => import("./pages/CreationSiteUckange.jsx"));
const CreationSiteMaizieresLesMetz = lazy(() => import("./pages/CreationSiteMaizieresLesMetz.jsx"));
const CreationSiteClouange = lazy(() => import("./pages/CreationSiteClouange.jsx"));
const CreationSiteRombas = lazy(() => import("./pages/CreationSiteRombas.jsx"));
const AgenceWebLuxembourg = lazy(() => import("./pages/AgenceWebLuxembourg.jsx"));
const AgenceWebEschSurAlzette = lazy(() => import("./pages/AgenceWebEschSurAlzette.jsx"));
const AgenceWebDifferdange = lazy(() => import("./pages/AgenceWebDifferdange.jsx"));
const AgenceWebDudelange = lazy(() => import("./pages/AgenceWebDudelange.jsx"));

// ✅ Pages principales dédiées (vraies routes pour SEO propre)
const Services = lazy(() => import("./pages/Services.jsx"));
const Portfolio = lazy(() => import("./pages/Portfolio.jsx"));
const Contact = lazy(() => import("./pages/Contact.jsx"));
const FAQ = lazy(() => import("./pages/FAQ.jsx"));

// ✅ Chargement immédiat uniquement pour la page d'accueil et pages légales
// ❌ Lazy loading pour toutes les pages de démo (économise ~400KB au chargement initial)

// Pizzeria Napoli Components - LAZY LOADED
const SonnerToaster = lazy(() => import("./components/ui/sonner").then(m => ({ default: m.Toaster })));
const PizzeriaHeader = lazy(() => import("./components/pizzeria-napoli/Header").then(m => ({ default: m.Header })));
const PizzeriaFooter = lazy(() => import("./components/pizzeria-napoli/Footer").then(m => ({ default: m.Footer })));
const PizzeriaHome = lazy(() => import("./pages/pizzeria-napoli/Home"));
const PizzeriaMenu = lazy(() => import("./pages/pizzeria-napoli/Menu"));
const PizzeriaAbout = lazy(() => import("./pages/pizzeria-napoli/About"));
const PizzeriaReviews = lazy(() => import("./pages/pizzeria-napoli/Reviews"));
const PizzeriaContact = lazy(() => import("./pages/pizzeria-napoli/Contact"));
const PizzeriaCart = lazy(() => import("./pages/pizzeria-napoli/Cart"));
const PizzeriaCheckout = lazy(() => import("./pages/pizzeria-napoli/Checkout"));

// Fast Food Components - LAZY LOADED
const FastFoodLayout = lazy(() => import("./components/fast-food/Layout"));
const FastFoodHome = lazy(() => import("./pages/fast-food/Home"));
const FastFoodMenu = lazy(() => import("./pages/fast-food/Menu"));
const FastFoodOrder = lazy(() => import("./pages/fast-food/Order"));
const FastFoodGallery = lazy(() => import("./pages/fast-food/Gallery"));
const FastFoodReviews = lazy(() => import("./pages/fast-food/Reviews"));
const FastFoodAbout = lazy(() => import("./pages/fast-food/About"));
const FastFoodContact = lazy(() => import("./pages/fast-food/Contact"));
const FastFoodLegal = lazy(() => import("./pages/fast-food/Legal"));

// Emerald Cuts (Barber) Components - LAZY LOADED
const EmeraldCutsHome = lazy(() => import("./pages/emerald-cuts/Home"));

// Demo banner component
const DemoBanner = () => {
  const navigate = useNavigate();
  
  return (
    <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-[#F87171] to-[#F56565] text-white py-3 px-4 shadow-lg">
      <div className="container mx-auto flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <span className="text-sm font-medium">
            🎭 Site de démonstration - Projet fictif pour présenter nos compétences
          </span>
        </div>
        <button
          onClick={() => navigate('/')}
          className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:scale-105 shadow-md"
          aria-label="Retour au site VyntraWeb"
        >
          <ArrowLeft size={16} />
          <span>Retour à VyntraWeb</span>
        </button>
      </div>
    </div>
  );
};

// Loading fallback pour lazy loading
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-900">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-[#F87171] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-white text-lg">Chargement...</p>
    </div>
  </div>
);

// Layout component for Pizzeria Napoli (avec Suspense)
const PizzeriaNapoliLayout = ({ children }) => (
  <Suspense fallback={<LoadingFallback />}>
    <DemoBanner />
    <div style={{ marginTop: '56px' }}>
      <PizzeriaHeader />
      <main className="min-h-screen">
        {children}
      </main>
      <PizzeriaFooter />
      <SonnerToaster />
    </div>
  </Suspense>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* ✅ Routes immédiates (page d'accueil) */}
          <Route path="/" element={<HomePage />} />
          
          {/* ✅ Pages légales lazy-loaded */}
          <Route path="/mentions-legales" element={
            <Suspense fallback={<LoadingFallback />}><MentionsLegales /></Suspense>
          } />
          <Route path="/politique-confidentialite" element={
            <Suspense fallback={<LoadingFallback />}><PolitiqueConfidentialite /></Suspense>
          } />
          <Route path="/cgv" element={
            <Suspense fallback={<LoadingFallback />}><CGV /></Suspense>
          } />

          {/* ✅ Pages SEO locales par ville */}
          <Route path="/creation-site-internet-hayange" element={
            <Suspense fallback={<LoadingFallback />}><CreationSiteHayange /></Suspense>
          } />
          <Route path="/creation-site-internet-metz" element={
            <Suspense fallback={<LoadingFallback />}><CreationSiteMetz /></Suspense>
          } />
          <Route path="/creation-site-internet-yutz" element={
            <Suspense fallback={<LoadingFallback />}><CreationSiteYutz /></Suspense>
          } />
          <Route path="/creation-site-internet-fameck" element={
            <Suspense fallback={<LoadingFallback />}><CreationSiteFameck /></Suspense>
          } />
          <Route path="/creation-site-internet-amneville" element={
            <Suspense fallback={<LoadingFallback />}><CreationSiteAmneville /></Suspense>
          } />
          <Route path="/creation-site-internet-florange" element={
            <Suspense fallback={<LoadingFallback />}><CreationSiteFlorange /></Suspense>
          } />
          <Route path="/creation-site-internet-terville" element={
            <Suspense fallback={<LoadingFallback />}><CreationSiteTerville /></Suspense>
          } />
          <Route path="/creation-site-internet-thionville" element={
            <Suspense fallback={<LoadingFallback />}><CreationSiteThionville /></Suspense>
          } />
          <Route path="/creation-site-internet-uckange" element={
            <Suspense fallback={<LoadingFallback />}><CreationSiteUckange /></Suspense>
          } />
          <Route path="/creation-site-internet-maizieres-les-metz" element={
            <Suspense fallback={<LoadingFallback />}><CreationSiteMaizieresLesMetz /></Suspense>
          } />
          <Route path="/creation-site-internet-clouange" element={
            <Suspense fallback={<LoadingFallback />}><CreationSiteClouange /></Suspense>
          } />
          <Route path="/creation-site-internet-rombas" element={
            <Suspense fallback={<LoadingFallback />}><CreationSiteRombas /></Suspense>
          } />
          <Route path="/agence-web-luxembourg" element={
            <Suspense fallback={<LoadingFallback />}><AgenceWebLuxembourg /></Suspense>
          } />
          <Route path="/agence-web-esch-sur-alzette" element={
            <Suspense fallback={<LoadingFallback />}><AgenceWebEschSurAlzette /></Suspense>
          } />
          <Route path="/agence-web-differdange" element={
            <Suspense fallback={<LoadingFallback />}><AgenceWebDifferdange /></Suspense>
          } />
          <Route path="/agence-web-dudelange" element={
            <Suspense fallback={<LoadingFallback />}><AgenceWebDudelange /></Suspense>
          } />

          {/* ✅ Pages principales dédiées (vraies routes pour SEO propre) */}
          <Route path="/services" element={
            <Suspense fallback={<LoadingFallback />}><Services /></Suspense>
          } />
          <Route path="/portfolio" element={
            <Suspense fallback={<LoadingFallback />}><Portfolio /></Suspense>
          } />
          <Route path="/contact" element={
            <Suspense fallback={<LoadingFallback />}><Contact /></Suspense>
          } />
          <Route path="/faq" element={
            <Suspense fallback={<LoadingFallback />}><FAQ /></Suspense>
          } />
          
          {/* ❌ Routes lazy-loaded (pages de démo) */}
          <Route path="/pizzeria-napoli" element={
            <Suspense fallback={<LoadingFallback />}>
              <PizzeriaNapoliLayout><PizzeriaHome /></PizzeriaNapoliLayout>
            </Suspense>
          } />
          <Route path="/pizzeria-napoli/menu" element={
            <Suspense fallback={<LoadingFallback />}>
              <PizzeriaNapoliLayout><PizzeriaMenu /></PizzeriaNapoliLayout>
            </Suspense>
          } />
          <Route path="/pizzeria-napoli/about" element={
            <Suspense fallback={<LoadingFallback />}>
              <PizzeriaNapoliLayout><PizzeriaAbout /></PizzeriaNapoliLayout>
            </Suspense>
          } />
          <Route path="/pizzeria-napoli/reviews" element={
            <Suspense fallback={<LoadingFallback />}>
              <PizzeriaNapoliLayout><PizzeriaReviews /></PizzeriaNapoliLayout>
            </Suspense>
          } />
          <Route path="/pizzeria-napoli/contact" element={
            <Suspense fallback={<LoadingFallback />}>
              <PizzeriaNapoliLayout><PizzeriaContact /></PizzeriaNapoliLayout>
            </Suspense>
          } />
          <Route path="/pizzeria-napoli/cart" element={
            <Suspense fallback={<LoadingFallback />}>
              <PizzeriaNapoliLayout><PizzeriaCart /></PizzeriaNapoliLayout>
            </Suspense>
          } />
          <Route path="/pizzeria-napoli/checkout" element={
            <Suspense fallback={<LoadingFallback />}>
              <PizzeriaNapoliLayout><PizzeriaCheckout /></PizzeriaNapoliLayout>
            </Suspense>
          } />

          {/* Fast Food Routes - Lazy Loaded */}
          <Route path="/fast-food" element={
            <Suspense fallback={<LoadingFallback />}>
              <FastFoodLayout hideHeader={true}><FastFoodHome /></FastFoodLayout>
            </Suspense>
          } />
          <Route path="/fast-food/menu" element={
            <Suspense fallback={<LoadingFallback />}>
              <FastFoodLayout><FastFoodMenu /></FastFoodLayout>
            </Suspense>
          } />
          <Route path="/fast-food/commande" element={
            <Suspense fallback={<LoadingFallback />}>
              <FastFoodLayout><FastFoodOrder /></FastFoodLayout>
            </Suspense>
          } />
          <Route path="/fast-food/galerie" element={
            <Suspense fallback={<LoadingFallback />}>
              <FastFoodLayout><FastFoodGallery /></FastFoodLayout>
            </Suspense>
          } />
          <Route path="/fast-food/avis" element={
            <Suspense fallback={<LoadingFallback />}>
              <FastFoodLayout><FastFoodReviews /></FastFoodLayout>
            </Suspense>
          } />
          <Route path="/fast-food/a-propos" element={
            <Suspense fallback={<LoadingFallback />}>
              <FastFoodLayout><FastFoodAbout /></FastFoodLayout>
            </Suspense>
          } />
          <Route path="/fast-food/contact" element={
            <Suspense fallback={<LoadingFallback />}>
              <FastFoodLayout><FastFoodContact /></FastFoodLayout>
            </Suspense>
          } />
          <Route path="/fast-food/mentions-legales" element={
            <Suspense fallback={<LoadingFallback />}>
              <FastFoodLayout><FastFoodLegal /></FastFoodLayout>
            </Suspense>
          } />

          {/* Emerald Cuts (Barber) Routes - Lazy Loaded */}
          <Route path="/emerald-cuts" element={
            <Suspense fallback={<LoadingFallback />}>
              <EmeraldCutsHome />
            </Suspense>
          } />
        </Routes>
        <Toaster />
      </BrowserRouter>
    </div>
  );
}

export default App;
